import { useEffect, useState } from "react";
import { Plus, Trash2, Save, Loader2 } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const DAYS = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"];
const DEFAULT_WH = Object.fromEntries(DAYS.map((_, d) => [String(d), {
  enabled: d < 5, ranges: [["09:00", "12:00"], ["13:30", "18:00"]],
}]));

const inputCls = "h-8 rounded-lg border border-slate-200 bg-white px-2 text-xs focus:outline-none focus:ring-2 focus:ring-slate-200";

export default function RdvAvailability() {
  const [wh, setWh] = useState(null);
  const [opts, setOpts] = useState({ duration: 60, durations: [], buffer: 0, minDelay: 1, maxDays: 30 });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/booking/config").then((r) => {
      const c = r.data || {};
      setWh(c.booking_work_hours || DEFAULT_WH);
      setOpts({ duration: c.booking_duration || 60, durations: c.booking_durations || [],
        buffer: c.booking_buffer_min || 0, minDelay: c.booking_min_delay_hours ?? 1,
        maxDays: c.booking_max_days_ahead || 30 });
    }).catch(() => setWh(DEFAULT_WH));
  }, []);

  const setDay = (d, patch) => setWh((w) => ({ ...w, [d]: { ...w[d], ...patch } }));
  const setRange = (d, i, j, v) => setWh((w) => {
    const ranges = w[d].ranges.map((r, k) => (k === i ? (j === 0 ? [v, r[1]] : [r[0], v]) : r));
    return { ...w, [d]: { ...w[d], ranges } };
  });

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/booking/config", {
        booking_work_hours: wh, booking_duration: Number(opts.duration) || 60,
        booking_durations: opts.durations, booking_buffer_min: Number(opts.buffer) || 0,
        booking_min_delay_hours: Number(opts.minDelay) || 0, booking_max_days_ahead: Number(opts.maxDays) || 30,
      });
      toast.success("Disponibilités enregistrées — le lien de réservation en tient compte immédiatement");
    } catch (e) { toast.error(apiError(e)); }
    setSaving(false);
  };

  if (!wh) return <div className="py-10 text-center text-sm text-slate-400">Chargement…</div>;

  const DURATIONS = [15, 30, 45, 60, 90, 120];

  return (
    <div className="max-w-3xl space-y-5" data-testid="rdv-dispos-panel">
      <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
        <h3 className="text-sm font-semibold text-slate-900 mb-1">Jours et horaires réservables</h3>
        <p className="text-xs text-slate-400 mb-4">Ces créneaux s'appliquent au lien de réservation en ligne, en tenant compte automatiquement de votre agenda DGA.</p>
        <div className="space-y-2">
          {DAYS.map((name, d) => {
            const conf = wh[String(d)] || { enabled: false, ranges: [] };
            return (
              <div key={d} className="flex flex-wrap items-center gap-2 border border-slate-100 rounded-xl px-3 py-2" data-testid={`rdv-dispo-day-${d}`}>
                <label className="flex items-center gap-2 w-28 cursor-pointer">
                  <input type="checkbox" checked={conf.enabled} data-testid={`rdv-dispo-toggle-${d}`}
                    onChange={(e) => setDay(String(d), { enabled: e.target.checked })}
                    className="rounded border-slate-300" />
                  <span className="text-sm font-medium text-slate-700">{name}</span>
                </label>
                {conf.enabled && (
                  <>
                    {conf.ranges.map((r, i) => (
                      <span key={i} className="flex items-center gap-1 text-xs text-slate-600">
                        <input type="time" value={r[0]} onChange={(e) => setRange(String(d), i, 0, e.target.value)}
                          className={inputCls} data-testid={`rdv-dispo-range-${d}-${i}-start`} />
                        <span className="text-slate-400">–</span>
                        <input type="time" value={r[1]} onChange={(e) => setRange(String(d), i, 1, e.target.value)}
                          className={inputCls} data-testid={`rdv-dispo-range-${d}-${i}-end`} />
                        {conf.ranges.length > 1 && (
                          <button onClick={() => setDay(String(d), { ranges: conf.ranges.filter((_, k) => k !== i) })}
                            className="p-1 rounded hover:bg-rose-50 text-slate-300 hover:text-rose-500 transition-colors"
                            data-testid={`rdv-dispo-range-${d}-${i}-del`}><Trash2 className="w-3 h-3" /></button>
                        )}
                      </span>
                    ))}
                    {conf.ranges.length < 4 && (
                      <button onClick={() => setDay(String(d), { ranges: [...conf.ranges, ["14:00", "17:00"]] })}
                        className="text-[11px] font-semibold text-[#0369A1] hover:underline flex items-center gap-0.5"
                        data-testid={`rdv-dispo-addrange-${d}`}><Plus className="w-3 h-3" />Plage</button>
                    )}
                  </>
                )}
                {!conf.enabled && <span className="text-xs text-slate-300">Fermé</span>}
              </div>
            );
          })}
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-[0_1px_3px_rgba(0,0,0,0.04)]" data-testid="rdv-dispos-rules">
        <h3 className="text-sm font-semibold text-slate-900 mb-4">Règles de réservation</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Durée par défaut</label>
            <select value={opts.duration} onChange={(e) => setOpts({ ...opts, duration: Number(e.target.value) })}
              className={`${inputCls} w-full mt-1 h-9`} data-testid="rdv-dispo-duration">
              {DURATIONS.map((d) => <option key={d} value={d}>{d >= 60 ? `${d / 60} h` : `${d} min`}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Temps entre deux RDV</label>
            <select value={opts.buffer} onChange={(e) => setOpts({ ...opts, buffer: Number(e.target.value) })}
              className={`${inputCls} w-full mt-1 h-9`} data-testid="rdv-dispo-buffer">
              <option value={0}>Aucun</option><option value={15}>15 min</option><option value={30}>30 min</option>
            </select>
          </div>
          <div>
            <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Délai minimum avant réservation (heures)</label>
            <input type="number" min={0} max={168} value={opts.minDelay}
              onChange={(e) => setOpts({ ...opts, minDelay: e.target.value })}
              className={`${inputCls} w-full mt-1 h-9`} data-testid="rdv-dispo-mindelay" />
          </div>
          <div>
            <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Jours maximum à l'avance</label>
            <input type="number" min={1} max={180} value={opts.maxDays}
              onChange={(e) => setOpts({ ...opts, maxDays: e.target.value })}
              className={`${inputCls} w-full mt-1 h-9`} data-testid="rdv-dispo-maxdays" />
          </div>
        </div>
        <div className="mt-4">
          <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Durées proposées au choix (facultatif)</label>
          <div className="flex flex-wrap gap-2 mt-1.5">
            {DURATIONS.map((d) => (
              <label key={d} className={`flex items-center gap-1.5 text-xs border rounded-lg px-2.5 py-1.5 cursor-pointer transition-colors ${
                opts.durations.includes(d) ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}
                data-testid={`rdv-dispo-durations-${d}`}>
                <input type="checkbox" className="hidden" checked={opts.durations.includes(d)}
                  onChange={(e) => setOpts({ ...opts, durations: e.target.checked ? [...opts.durations, d] : opts.durations.filter((x) => x !== d) })} />
                {d >= 60 ? `${d / 60} h` : `${d} min`}
              </label>
            ))}
          </div>
          <p className="text-[11px] text-slate-400 mt-1.5">Si plusieurs durées sont cochées, le visiteur choisit la sienne sur la page de réservation.</p>
        </div>
        <div className="flex justify-end mt-4">
          <Button className="bg-[#0B132B] hover:bg-[#1C2A4F] text-white rounded-xl gap-1.5 text-xs" onClick={save} disabled={saving} data-testid="rdv-dispos-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}Enregistrer
          </Button>
        </div>
      </div>
    </div>
  );
}
